import { useCallback, useEffect, useRef, useState } from 'react';
import { getVfdConfiguration } from '../services/vfdConfigurationService';
import vfdAgentClient from '../services/vfdAgentClient';

const money = (value) => Number(value || 0).toFixed(2);
const quantity = (cart) => (cart || []).reduce((sum, item) => sum + Number(item.quantite || 0), 0);
const amountLine = (label, value, currency, columns = 20) => {
  const width = Math.max(8, Number(columns) || 20);
  const suffix = `${money(value)} ${currency || ''}`.trim();
  if (suffix.length >= width) return suffix.slice(-width);
  const clippedLabel = String(label || '').slice(0, Math.max(0, width - suffix.length - 1));
  return `${clippedLabel}${' '.repeat(width - clippedLabel.length - suffix.length)}${suffix}`;
};

export default function useVfdDisplay({ pointOfSaleId, cart, total, paymentOpen, paidAmount }) {
  const [status, setStatus] = useState({ state: 'disabled', error: null });
  const configRef = useRef(null);
  const previousRef = useRef([]);
  const timerRef = useRef(null);
  const paymentTimerRef = useRef(null);
  const suppressNextEmptyRef = useRef(false);
  const logRef = useRef(0);

  const safely = useCallback(async (operation) => {
    const config = configRef.current;
    if (!config?.enabled) return null;
    try { const result = await operation(config); setStatus({ state: result?.connected === false ? 'disconnected' : 'connected', error: result?.lastError || null }); return result; }
    catch (error) { setStatus({ state: 'unavailable', error: error?.response?.data?.error || error.message }); if (Date.now() - logRef.current > 30000) { console.warn('VFD agent unavailable:', error.message); logRef.current = Date.now(); } return null; }
  }, []);

  const showTotal = useCallback(() => safely((c) => c.showTotalOnCartChange && vfdAgentClient.display(c, {
    line1: amountLine('TOTAL', total, c.currency, c.columns),
    line2: `${quantity(cart)} ARTICLE(S)`, returnToIdle: false,
  })), [cart, safely, total]);

  useEffect(() => {
    if (!pointOfSaleId) return undefined;
    let active = true;
    getVfdConfiguration(pointOfSaleId).then(({ data }) => {
      if (!active) return; configRef.current = data;
      if (!data.enabled) { setStatus({ state: 'disabled', error: null }); return; }
      safely(async (c) => { await vfdAgentClient.connect(c); return vfdAgentClient.welcome(c); });
    }).catch((error) => active && setStatus({ state: 'configuration-error', error: error.message }));
    return () => { active = false; clearTimeout(timerRef.current); clearTimeout(paymentTimerRef.current); };
  }, [pointOfSaleId, safely]);

  useEffect(() => {
    const c = configRef.current; if (!c?.enabled) return;
    clearTimeout(timerRef.current);
    if (!cart?.length) {
      previousRef.current = [];
      if (suppressNextEmptyRef.current) { suppressNextEmptyRef.current = false; return; }
      safely((cfg) => vfdAgentClient.welcome(cfg)); return;
    }
    const changed = cart.find((item) => { const old=previousRef.current.find((x)=>String(x.articleId)===String(item.articleId));return !old||Number(old.quantite)!==Number(item.quantite)||Number(old.montantTTC)!==Number(item.montantTTC); });
    previousRef.current = cart.map((item) => ({ ...item }));
    if (changed && c.showItemOnScan) {
      safely((cfg) => vfdAgentClient.display(cfg, { line1: changed.articleNom || changed.articleCode || 'ARTICLE', line2: `${changed.quantite} x ${money(changed.prixUnitaireTTC)} = ${money(changed.montantTTC)}`, returnToIdle:false }));
      timerRef.current = setTimeout(showTotal, 1200);
    } else timerRef.current = setTimeout(showTotal, 180);
    return () => clearTimeout(timerRef.current);
  }, [cart, safely, showTotal]);

  useEffect(() => {
    const c=configRef.current;clearTimeout(paymentTimerRef.current);if(paymentOpen)clearTimeout(timerRef.current);if(!c?.enabled||!paymentOpen||!c.showPaidAndChange)return;
    const paid=Number(paidAmount||0),due=Number(total||0),difference=paid-due;
    paymentTimerRef.current=setTimeout(()=>safely((cfg)=>vfdAgentClient.display(cfg,{line1:amountLine('PAYE',paid,cfg.currency,cfg.columns),line2:difference>=0?amountLine('MONNAIE',difference,cfg.currency,cfg.columns):amountLine('RESTE',-difference,cfg.currency,cfg.columns),returnToIdle:false})),120);
    return()=>clearTimeout(paymentTimerRef.current);
  }, [paidAmount,paymentOpen,safely,total]);

  const saleCompleted = useCallback(() => { clearTimeout(timerRef.current);clearTimeout(paymentTimerRef.current);suppressNextEmptyRef.current = true; return safely((c) => vfdAgentClient.display(c, { line1:c.thankYouLine1,line2:c.thankYouLine2,durationMs:c.thankYouDurationSeconds*1000,returnToIdle:true })); }, [safely]);
  const saleCancelled = useCallback(() => { clearTimeout(timerRef.current);clearTimeout(paymentTimerRef.current);return safely((c) => vfdAgentClient.welcome(c)); }, [safely]);
  return { status, saleCompleted, saleCancelled };
}
